<script setup lang="ts">
/**
 * Wallpaper (Raster) export panel — brief 01-export.md Mode 2's
 * "How many tiles?" picker. See wallpaperExport.ts for why this is a single
 * "tiles across" shorthand rather than independent rows/columns.
 */
import { ref } from 'vue';
import { IconChevronLeft, IconLock } from '@tabler/icons-vue';
import { exportWallpaper } from '@/services/wallpaperExport';
import { useEntitlementStore } from '@/stores/entitlement';
import { useUiStore } from '@/stores/ui';

const emit = defineEmits<{ back: []; done: [] }>();

const entitlement = useEntitlementStore();
const ui = useUiStore();

const PRESETS = [2, 3, 4] as const;
const tilesAcross = ref(3);
const customOpen = ref(false);
const customValue = ref(5);
const includeBackground = ref(true);

// Brief 12-vector_draw: free mode gets exactly 2×2/3×3; 4×4 and any custom
// size stay pro-only.
function pickPreset(n: number): void {
  if (!entitlement.canExportWallpaperSize(n)) {
    ui.openPaywall('export');
    return;
  }
  customOpen.value = false;
  tilesAcross.value = n;
}

function pickCustom(): void {
  if (!entitlement.canExport) {
    ui.openPaywall('export');
    return;
  }
  customOpen.value = true;
  tilesAcross.value = customValue.value;
}

function doExport(): void {
  if (!entitlement.canExportWallpaperSize(tilesAcross.value)) {
    ui.openPaywall('export');
    return;
  }
  exportWallpaper({ tilesAcross: tilesAcross.value, background: includeBackground.value });
  emit('done');
}
</script>

<template>
  <div class="panel">
    <button class="back-link" @click="emit('back')">
      <IconChevronLeft :size="15" stroke-width="2" />
      Back
    </button>

    <p class="lead">Renders the full repeating pattern as one flat PNG, at current layer settings.</p>

    <div class="seg">
      <button
        v-for="n in PRESETS"
        :key="n"
        class="seg-btn"
        :class="{ active: !customOpen && tilesAcross === n }"
        @click="pickPreset(n)"
      >
        {{ n }}×{{ n }}
        <IconLock v-if="!entitlement.canExportWallpaperSize(n)" :size="12" stroke-width="2" />
      </button>
      <button class="seg-btn" :class="{ active: customOpen }" @click="pickCustom">
        Custom
        <IconLock v-if="!entitlement.canExport" :size="12" stroke-width="2" />
      </button>
    </div>

    <label v-if="customOpen" class="custom-row">
      <span>Tiles across</span>
      <input v-model.number="customValue" type="number" min="1" max="12" @change="tilesAcross = customValue" />
    </label>

    <label class="toggle-row">
      <input v-model="includeBackground" type="checkbox" />
      <span>Include background colour</span>
    </label>

    <button class="primary-btn" @click="doExport">Export PNG</button>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 12.5px;
  cursor: pointer;
  padding: 0;
}

.back-link:hover {
  color: var(--text-primary);
}

.lead {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.5;
}

.seg {
  display: flex;
  gap: 2px;
  background: color-mix(in srgb, var(--ui-border) 35%, transparent);
  border: 1px solid var(--ui-border);
  border-radius: 9px;
  padding: 2px;
}

.seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 34px;
  padding: 4px 14px;
  font-size: 13px;
  background: transparent;
  color: var(--text-primary);
  border: none;
  border-radius: 7px;
  cursor: pointer;
}

.seg-btn svg {
  color: var(--text-muted);
}

.seg-btn.active {
  background: color-mix(in srgb, var(--accent) 22%, transparent);
}

.custom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: var(--text-primary);
}

.custom-row input {
  width: 64px;
  min-height: 32px;
  padding: 4px 8px;
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 6px;
  color: var(--text-primary);
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-primary);
  cursor: pointer;
}

.toggle-row input {
  width: 16px;
  height: 16px;
  accent-color: var(--accent);
  cursor: pointer;
}

.primary-btn {
  min-height: 42px;
  background: var(--accent);
  color: var(--accent-contrast);
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
</style>
